import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './UserProfilePage.module.css';
import { useAuth } from '../contexts/AuthContext';
import { UserProfile } from '../types/user';
import { getUserProfileByUsername, isUsernameValid, isUsernameAvailable, updateUserProfile, uploadUserAvatar, listCommunityPostsByAuthor } from '../services/firebase';

// useParams generic'i doğrudan string index ile kullanılacak

// Basit username doğrulama (sadece küçük harf ve rakam, 3-24 char)
// const USERNAME_REGEX = /^[a-z0-9]{3,24}$/; // ileride oluşturma/düzenlemede kullanılacak

export default function UserProfilePage() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { userProfile: currentUserProfile, refreshUserProfile, isLoggedIn } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [displayNameDraft, setDisplayNameDraft] = useState('');
  const [usernameDraft, setUsernameDraft] = useState('');
  const [phoneDraft, setPhoneDraft] = useState('');
  const [photoDraft, setPhotoDraft] = useState('');
  const [usernameStatus, setUsernameStatus] = useState<'idle'|'checking'|'valid'|'taken'|'invalid'>('idle');
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [privacyDraft, setPrivacyDraft] = useState<'public'|'members'|'private'>('public');
  const [avatarVisDraft, setAvatarVisDraft] = useState<'everyone'|'members'|'private'>('everyone');
  // Community posts integration
  const [postsLoading, setPostsLoading] = useState(false);
  const [posts, setPosts] = useState<any[]>([]);
  const [postsError, setPostsError] = useState<string|null>(null);
  const [postsLastDoc, setPostsLastDoc] = useState<any>(null);
  const [postsHasMore, setPostsHasMore] = useState(false);
  const [activeTab, setActiveTab] = useState<'info'|'posts'>('info');

  const isOwn = !!currentUserProfile && profile?.uid === currentUserProfile.uid;

  const load = useCallback(async () => {
    if (!username) return;
    setLoading(true); setNotFound(false);
    try {
      const prof = await getUserProfileByUsername(username);
      if (!prof) {
        setNotFound(true);
      } else {
        setProfile(prof);
        if (prof && isOwn === false) {
          // ensure drafts reflect other profile only when not own (no edit)
        }
      }
    } catch (e) {
      setNotFound(true);
    } finally { setLoading(false); }
  }, [username]);

  useEffect(() => { load(); }, [load]);

  useEffect(()=>{
    if (profile && isOwn) {
      setDisplayNameDraft(profile.displayName || '');
      setUsernameDraft(profile.username || (username||''));
      setPhoneDraft(profile.phoneNumber || '');
      setPhotoDraft(profile.photoURL || '');
  setPrivacyDraft((profile as any).privacy || 'public');
  setAvatarVisDraft((profile as any).avatarVisibility || 'everyone');
  setUsernameStatus('idle');
      setErrorMsg(null);
      setSuccessMsg(null);
    }
  }, [profile, isOwn, username]);

  // Load community posts when switching to posts tab
  const loadPosts = useCallback(async (reset=false) => {
    if (!profile) return;
    try {
      setPostsLoading(true);
      const res = await listCommunityPostsByAuthor({ authorId: profile.uid, pageSize:6, lastDoc: reset? null : postsLastDoc });
      setPosts(p => reset ? res.items : [...p, ...res.items]);
      setPostsLastDoc(res.lastDoc);
      setPostsHasMore(res.hasMore);
      setPostsError(null);
    } catch(e:any){ setPostsError(e.message || 'Gönderiler alınamadı'); }
    finally { setPostsLoading(false); }
  }, [profile, postsLastDoc]);

  useEffect(()=>{ if (activeTab==='posts' && isLoggedIn) { loadPosts(true); } }, [activeTab, loadPosts, isLoggedIn]);

  // Username availability check (debounced)
  useEffect(()=>{
    if (!editMode) return; 
    if (!isOwn) return;
    if (!usernameDraft) { setUsernameStatus('idle'); return; }
    if (!isUsernameValid(usernameDraft)) { setUsernameStatus('invalid'); return; }
    if (profile && profile.username === usernameDraft) { setUsernameStatus('idle'); return; }
    let active = true;
    setUsernameStatus('checking');
    const t = setTimeout(async ()=>{
      try {
        const available = await isUsernameAvailable(usernameDraft);
        if (!active) return;
        setUsernameStatus(available ? 'valid' : 'taken');
      } catch {
        if (!active) return;
        setUsernameStatus('taken');
      }
    }, 500);
    return ()=>{ active = false; clearTimeout(t); };
  }, [usernameDraft, editMode, isOwn, profile]);

  const canSave = editMode && isOwn && !saving && (
    (displayNameDraft.trim() !== (profile?.displayName||'')) ||
    (photoDraft.trim() !== (profile?.photoURL||'')) ||
    (phoneDraft.trim() !== (profile?.phoneNumber||'')) ||
    (usernameDraft.trim() !== (profile?.username||''))
  || (privacyDraft !== ((profile as any).privacy || 'public'))
  || (avatarVisDraft !== ((profile as any).avatarVisibility || 'everyone'))
  ) && (usernameStatus !== 'checking' && usernameStatus !== 'invalid' && usernameStatus !== 'taken');

  const handleSave = async () => {
    if (!profile || !isOwn) return;
    setSaving(true); setErrorMsg(null); setSuccessMsg(null);
    try {
      const updates: any = {};
      if (displayNameDraft.trim() !== (profile.displayName||'')) updates.displayName = displayNameDraft.trim();
      if (photoDraft.trim() !== (profile.photoURL||'')) updates.photoURL = photoDraft.trim() || null;
      if (phoneDraft.trim() !== (profile.phoneNumber||'')) updates.phoneNumber = phoneDraft.trim() || undefined;
      let usernameChanged = false;
      if (usernameDraft.trim() && usernameDraft.trim() !== (profile.username||'')) {
        if (!isUsernameValid(usernameDraft.trim())) throw new Error('Geçersiz kullanıcı adı');
        if (usernameStatus === 'taken' || usernameStatus === 'invalid' || usernameStatus === 'checking') throw new Error('Kullanıcı adı uygun değil');
        updates.username = usernameDraft.trim();
        usernameChanged = true;
      }
  if (privacyDraft !== ((profile as any).privacy || 'public')) updates.privacy = privacyDraft;
  if (avatarVisDraft !== ((profile as any).avatarVisibility || 'everyone')) updates.avatarVisibility = avatarVisDraft;
      if (Object.keys(updates).length === 0) { setSaving(false); setEditMode(false); return; }
      await updateUserProfile(profile.uid, updates);
      await refreshUserProfile();
      setSuccessMsg('Profil güncellendi');
      setEditMode(false);
      // Reload profile view (fetch updated by new username if changed)
      if (usernameChanged) {
        navigate(`/profile/${updates.username}`);
      } else {
        await load();
      }
    } catch (e:any) {
      setErrorMsg(e.message || 'Kaydetme hatası');
    } finally {
      setSaving(false);
      setTimeout(()=>{ setSuccessMsg(null); }, 2500);
    }
  };

  if (loading) {
    return <div className={styles.profilePage}>Yükleniyor...</div>;
  }
  if (notFound) {
    return <div className={styles.profilePage}>Kullanıcı bulunamadı.</div>;
  }
  if (!profile) return null;

  // Privacy enforcement: if profile is private and not owner => show restricted message.
  const targetPrivacy = (profile as any).privacy || 'public';
  if (!isOwn) {
    if (targetPrivacy === 'private') {
      return <div className={styles.profilePage}><div className={styles.restrictedBox}>Bu profil gizli.</div></div>;
    }
    if (targetPrivacy === 'members' && !currentUserProfile) {
      return <div className={styles.profilePage}><div className={styles.restrictedBox}>Bu profil sadece üyeler tarafından görüntülenebilir.</div></div>;
    }
  }

  return (
    <div className={styles.profilePage}>
      <div className={styles.headerRow}>
        <div className={styles.avatarWrapper}>
          {(() => {
            const canShowAvatar = (() => {
              const setting = (profile as any).avatarVisibility || 'everyone';
              if (profile.uid === currentUserProfile?.uid) return true; // owner always sees
              if (setting === 'everyone') return true;
              if (setting === 'members' && currentUserProfile) return true;
              return false; // private
            })();
            if (canShowAvatar && profile.photoURL) {
              return <img src={profile.photoURL} alt={profile.displayName || profile.uid} />;
            }
            return <div className={styles.fallbackAvatar}>{(profile.displayName || 'K').charAt(0).toUpperCase()}</div>;
          })()}
          {isOwn && (
            <label className={styles.editAvatarBtn}>
              {uploadingAvatar ? 'Yükleniyor...' : 'Avatar'}
              <input
                type="file"
                accept="image/*"
                style={{ display:'none' }}
                onChange={async (e)=>{
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setUploadingAvatar(true); setErrorMsg(null); setSuccessMsg(null);
                  try {
                    const url = await uploadUserAvatar(profile.uid, file);
                    setPhotoDraft(url);
                    await refreshUserProfile();
                    await load();
                    setSuccessMsg('Avatar güncellendi');
                  } catch (er:any) {
                    setErrorMsg(er.message || 'Avatar yüklenemedi');
                  } finally {
                    setUploadingAvatar(false);
                    setTimeout(()=> setSuccessMsg(null), 2500);
                  }
                }}
              />
            </label>
          )}
        </div>
        <div className={styles.primaryInfo}>
          <div className={styles.username}>@{username}</div>
          {editMode && isOwn ? (
            <>
              <input
                value={displayNameDraft}
                onChange={e=>setDisplayNameDraft(e.target.value)}
                className={styles.inlineEdit}
                placeholder="Görünen ad"
              />
              <div className={styles.editFieldGroup}>
                <label className={styles.smallLabel}>Kullanıcı Adı</label>
                <input
                  value={usernameDraft}
                  onChange={e=>setUsernameDraft(e.target.value.toLowerCase())}
                  className={styles.inlineEdit}
                  placeholder="kullanıcı adı"
                />
                {usernameStatus !== 'idle' && (
                  <div className={
                    usernameStatus === 'valid' ? styles.statusOk :
                    usernameStatus === 'taken' ? styles.statusError :
                    usernameStatus === 'invalid' ? styles.statusError : styles.statusInfo
                  }>
                    {usernameStatus === 'checking' && 'Kontrol ediliyor...'}
                    {usernameStatus === 'valid' && 'Uygun'}
                    {usernameStatus === 'taken' && 'Alınmış'}
                    {usernameStatus === 'invalid' && 'Geçersiz format'}
                  </div>
                )}
              </div>
              <div className={styles.editFieldGroup}>
                <label className={styles.smallLabel}>Gizlilik</label>
                <select
                  value={privacyDraft}
                  onChange={e=>setPrivacyDraft(e.target.value as any)}
                  className={styles.inlineEdit}
                >
                  <option value="public">Herkese Açık</option>
                  <option value="members">Sadece Üyeler</option>
                  <option value="private">Gizli</option>
                </select>
                <div className={styles.statusInfo}>
                  {privacyDraft === 'public' && 'Profil herkes tarafından görülebilir.'}
                  {privacyDraft === 'members' && 'Sadece giriş yapmış kullanıcılar görebilir.'}
                  {privacyDraft === 'private' && 'Sadece sen görebilirsin.'}
                </div>
              </div>
              <div className={styles.editFieldGroup}>
                <label className={styles.smallLabel}>Avatar Görünürlüğü</label>
                <select
                  value={avatarVisDraft}
                  onChange={e=> setAvatarVisDraft(e.target.value as any)}
                  className={styles.inlineEdit}
                >
                  <option value="everyone">Herkes</option>
                  <option value="members">Üyeler</option>
                  <option value="private">Sadece Ben</option>
                </select>
                <div className={styles.statusInfo}>
                  {avatarVisDraft === 'everyone' && 'Avatar tüm ziyaretçilere gösterilir.'}
                  {avatarVisDraft === 'members' && 'Avatar sadece giriş yapmış kullanıcılara gösterilir.'}
                  {avatarVisDraft === 'private' && 'Avatar sadece sana gösterilir, diğerleri baş harf görür.'}
                </div>
              </div>
            </>
          ) : (
            <div className={styles.displayName}>{profile.displayName}</div>
          )}
          <div className={styles.badgeRow}>
            {profile.isPremium && <span className={styles.badge}>PREMIUM</span>}
            {profile.role === 'admin' && <span className={styles.badge}>ADMIN</span>}
          </div>
        </div>
        <div className={styles.actionsBar}>
          {isOwn && !editMode && (
            <button className={styles.actionBtn} onClick={()=>{ setEditMode(true); }}>Profili Düzenle</button>
          )}
          {isOwn && editMode && (
            <>
              <button className={styles.actionBtn} disabled={saving} onClick={()=>{ setEditMode(false); }}>İptal</button>
              <button className={styles.actionBtn} disabled={!canSave} onClick={handleSave}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
            </>
          )}
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.tabHeader}>
          <button type="button" className={activeTab==='info'? styles.activeTabBtn: styles.tabBtn} onClick={()=>setActiveTab('info')}>Profil</button>
          {isLoggedIn && <button type="button" className={activeTab==='posts'? styles.activeTabBtn: styles.tabBtn} onClick={()=>setActiveTab('posts')}>Gönderiler</button>}
        </div>
        {activeTab==='info' && (
        <>
        <div className={styles.sectionTitle}>Genel Bilgiler</div>
        <div className={styles.fieldGrid}>
          <div className={styles.field}><label>E-posta</label><div className={styles.value}>{profile.email || <span className={styles.muted}>—</span>}</div></div>
          <div className={styles.field}><label>Rol</label><div className={styles.value}>{profile.role}</div></div>
          <div className={styles.field}><label>Premium</label><div className={styles.value}>{profile.isPremium ? 'Evet' : 'Hayır'}</div></div>
          <div className={styles.field}><label>Katılım</label><div className={styles.value}>{new Date(profile.createdAt).toLocaleDateString()}</div></div>
          {profile.premiumSince && <div className={styles.field}><label>Premium Sezon</label><div className={styles.value}>{new Date(profile.premiumSince).toLocaleDateString()}</div></div>}
          {!editMode && <div className={styles.field}><label>Gizlilik</label><div className={styles.value}>{targetPrivacy === 'public' ? 'Herkese Açık' : targetPrivacy === 'members' ? 'Sadece Üyeler' : 'Gizli'}</div></div>}
          {editMode && isOwn ? (
            <>
              <div className={styles.field}><label>Telefon</label>
                <input
                  className={styles.inlineEdit}
                  value={phoneDraft}
                  onChange={e=>setPhoneDraft(e.target.value)}
                  placeholder="Telefon numarası"
                />
              </div>
              <div className={styles.field}><label>Fotoğraf URL</label>
                <input
                  className={styles.inlineEdit}
                  value={photoDraft}
                  onChange={e=>setPhotoDraft(e.target.value)}
                  placeholder="https://..."
                />
                {photoDraft && (
                  <div className={styles.photoPreviewRow}>
                    <img src={photoDraft} alt="preview" className={styles.photoPreview} onError={(ev)=>{ (ev.currentTarget as HTMLImageElement).style.opacity='0.2'; }} />
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {profile.phoneNumber && <div className={styles.field}><label>Telefon</label><div className={styles.value}>{profile.phoneNumber}</div></div>}
              {profile.photoURL && <div className={styles.field}><label>Fotoğraf</label><div className={styles.value}><span className={styles.muted}>Görüntüleniyor</span></div></div>}
            </>
          )}
        </div>
        {errorMsg && <div className={styles.errorBox}>{errorMsg}</div>}
        {successMsg && <div className={styles.successBox}>{successMsg}</div>}
        </>
        )}
  {isLoggedIn && activeTab==='posts' && (
          <div className={styles.postsWrapper}>
            <div className={styles.sectionTitle}>Gönderiler</div>
            {postsError && <div className={styles.errorBox}>{postsError}</div>}
            {postsLoading && posts.length===0 && <div className={styles.loadingState}>Yükleniyor...</div>}
            {!postsLoading && posts.length===0 && <div className={styles.emptyState}>Henüz gönderi yok.</div>}
            <div className={styles.postGrid}>
              {posts.map(p => (
                <div key={p.id} className={styles.postMiniCard}>
                  <div className={styles.postMiniTop}>
                    <div className={styles.postMiniTime}>{new Date(p.createdAt).toLocaleDateString()} · {p.likesCount}👍 · {p.commentsCount}💬</div>
                  </div>
                  <div className={styles.postMiniContent}>{p.content.length>140? p.content.slice(0,140)+'…' : p.content}</div>
                  {p.tags && p.tags.length>0 && <div className={styles.postMiniTags}>{p.tags.slice(0,4).map((t:string)=> <span key={t}>#{t}</span>)}</div>}
                </div>
              ))}
            </div>
            {postsHasMore && <div className={styles.loadMoreArea}><button className={styles.loadMorePostsBtn} disabled={postsLoading} onClick={()=>loadPosts(false)}>{postsLoading? 'Yükleniyor...' : 'Daha Fazla'}</button></div>}
          </div>
        )}
      </div>
    </div>
  );
}
